// Field Model - Valtio-based reactive state management
import {proxy, ref} from "valtio";

import {BaseField, BaseFieldConfig} from "./base-field";
import {CursorPosition, IValueTransformer, ValidationError} from "./types";

export interface FieldConfig extends BaseFieldConfig {
    initialValue?: string;
    transformers?: Array<IValueTransformer>;
}

class FieldClass extends BaseField {
    // Reactive state properties
    _value: string;
    _cursorPosition: CursorPosition | null = null;

    // Public properties
    readonly initialValue: string;
    readonly transformers: Array<IValueTransformer>;

    constructor(config: FieldConfig = {}) {
        super(config);

        this.initialValue = config.initialValue || "";
        this._value = this.initialValue;
        this.transformers = ref(config.transformers || []) as Array<IValueTransformer>;
    }

    // Computed getters
    get value(): string {
        return this._value;
    }

    get cursorPosition(): CursorPosition | null {
        return this._cursorPosition;
    }

    // Cursor position methods
    setCursorPosition(start: number, end: number): void {
        this._cursorPosition = {start, end};
    }

    clearCursorPosition(): void {
        this._cursorPosition = null;
    }

    // Actions
    setValue(value: string, silent: boolean = false): void {
        // Apply transformers
        let transformedValue = value;
        let cursor = this._cursorPosition;

        for (const transformer of this.transformers) {
            const valueBefore = transformedValue;

            // Check if transformer has validation BEFORE transform
            if (transformer.validate) {
                const error = transformer.validate(transformedValue);
                if (error) {
                    this._errors = [error];
                    // Adjust cursor if transformer supports it
                    if (transformer.adjustCursor && cursor) {
                        cursor = transformer.adjustCursor(cursor, valueBefore);
                    }
                    // Still apply transform but show error
                    transformedValue = transformer.transform(transformedValue);
                    this._value = transformedValue;
                    this._cursorPosition = cursor;
                    this.events.emit("error", error);
                    if (!silent) {
                        this.events.emit("change", this._value);
                    }
                    return;
                }
            }

            // Adjust cursor position if transformer supports it
            if (transformer.adjustCursor && cursor) {
                cursor = transformer.adjustCursor(cursor, valueBefore);
            }

            transformedValue = transformer.transform(transformedValue);
        }

        this._value = transformedValue;
        this._cursorPosition = cursor;
        this._errors = [];

        if (!silent) {
            this.events.emit("change", this._value);
        }
    }

    setError(error: ValidationError | null): void {
        if (error === null) {
            this._errors = [];
        } else {
            this._errors = [error];
            this.events.emit("error", error);
        }
    }

    setErrors(errors: Array<ValidationError>): void {
        this._errors = errors;
        if (errors.length > 0) {
            this.events.emit("error", errors[0]);
        }
    }

    enable(): void {
        this._disabled = false;
    }

    disable(): void {
        this._disabled = true;
    }

    validate(): {isValid: boolean; error?: string} {
        const errors: Array<ValidationError> = [];

        for (const validator of this.validators) {
            const result = validator.validate(this._value);
            if (!result.isValid) {
                errors.push({
                    type: "error",
                    message: result.error || validator.message,
                    messageParams: result.errorParams || validator.messageParams,
                });
            }
        }

        this._errors = errors;

        const isValid = errors.length === 0;
        this.events.emit("valid", isValid);

        return {
            isValid,
            error: errors[0]?.message,
        };
    }

    clear(): void {
        this.setValue("", true);
        this._errors = [];
    }

    reset(): void {
        this._value = this.initialValue;
        this._errors = [];
        this.events.emit("change", this._value);
    }
}

// Export class for inheritance
export {FieldClass};

// Export type for the proxied Field
export type FieldInstance = FieldClass;

// Factory function that returns a proxied instance (SSR compatible)
export function Field(config?: FieldConfig): FieldInstance {
    return proxy<FieldInstance>(new FieldClass(config));
}
